import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { listMyClasses } from '../api/user/class.api';
import { getMyProfile } from '../api/user/account.api';
import OnboardingModal from '../components/OnboardingModal';
import PrivacyConsentModal from '../components/PrivacyConsentModal';
import { setSelectedClass } from './utill';

/**
 * 보호된 라우트 래퍼.
 *  1) access_token 없으면 /login으로 리다이렉트
 *  2) 프로필의 핵심 정보(user_field, purpose)가 모두 비어있으면 = 첫 로그인 → 정보수집 모달
 *     (basic class에 자동 등록되어 있더라도, 사용자가 직접 정보를 입력했는지 여부로 판정)
 *  3) basic class도 미등록이면 OnboardingModal에서 redeem까지 자동 호출
 *  4) 모달은 강제 표시 — 완료해야 자식 라우트 사용 가능
 *
 * 캐싱: 한 번 enrolled로 확정되면 sessionStorage('onboarding_resolved')에 플래그를 두어
 * 같은 세션 동안 라우트 전환마다 /user/me + /user/classes를 다시 부르지 않는다.
 * 플래그는 sessionStorage.clear() 기반 로그아웃과 client.js의 401 인터셉터에서 함께 제거된다.
 */
const RESOLVED_KEY = 'onboarding_resolved';

export default function ProtectedRoute({ children }) {
  const accessToken = sessionStorage.getItem('access_token');
  const basicClassId = process.env.REACT_APP_BASIC_CLASS_ID;
  const cachedResolved = accessToken && sessionStorage.getItem(RESOLVED_KEY) === 'true';

  // 'checking' | 'enrolled' | 'need-onboarding'
  const [status, setStatus] = useState(() => {
    if (!accessToken) return 'redirect';
    return cachedResolved ? 'enrolled' : 'checking';
  });
  // 첫 로그인 시 basic class에 이미 등록되어 있는지 여부 — OnboardingModal에 전달
  const [alreadyInBasicClass, setAlreadyInBasicClass] = useState(false);

  useEffect(() => {
    if (!accessToken) return undefined;
    // 이미 같은 세션에서 onboarding/basic class 검사를 통과했으면 재요청 스킵
    if (sessionStorage.getItem(RESOLVED_KEY) === 'true') {
      if (basicClassId) setSelectedClass(basicClassId);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const [profile, classesData] = await Promise.all([
          getMyProfile().catch(() => null),
          basicClassId ? listMyClasses({ limit: 100 }).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        // 정보수집 완료 여부 — 두 필드 모두 채워져 있어야 완료로 간주
        const hasField = !!(profile?.user_field && String(profile.user_field).trim());
        const hasPurpose = !!(profile?.purpose && String(profile.purpose).trim());
        const onboardingDone = hasField && hasPurpose;

        // basic class 등록 여부
        const items = Array.isArray(classesData?.items) ? classesData.items : [];
        const hasBasic = basicClassId
          ? items.some((c) => String(c.class_id) === String(basicClassId))
          : true;

        setAlreadyInBasicClass(hasBasic);

        if (onboardingDone) {
          if (hasBasic && basicClassId) setSelectedClass(basicClassId);
          sessionStorage.setItem(RESOLVED_KEY, 'true');
          setStatus('enrolled');
        } else {
          setStatus('need-onboarding');
        }
      } catch {
        if (cancelled) return;
        // 검사 실패 시 화면 차단을 막기 위해 통과 (플래그는 set 하지 않음 → 다음 진입 시 재시도)
        setStatus('enrolled');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, basicClassId]);

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  if (status === 'checking') {
    return null;
  }

  return (
    <>
      {children}
      <OnboardingModal
        open={status === 'need-onboarding'}
        skipRedeem={alreadyInBasicClass}
        onCompleted={() => {
          if (basicClassId) setSelectedClass(basicClassId);
          sessionStorage.setItem(RESOLVED_KEY, 'true');
          // 사용자가 모달에서 이름/소속 등을 새로 입력했을 수 있으므로 헤더 캐시는 무효화 → 재fetch 유도
          sessionStorage.removeItem('header_profile_cache');
          setStatus('enrolled');
        }}
      />
      {/* 동의 정보 없는 기존 회원 대상 약관 동의 모달. 온보딩 완료(enrolled) 후에만 활성화. */}
      <PrivacyConsentModal active={status === 'enrolled'} />
    </>
  );
}
